import getAuthClient from 'Tasking_Helpers/getAuthClient';

class FedExAddressVerificationService {
  constructor() {
    // Use PyxIQ backend API to proxy FedEx requests (avoids CORS and keeps credentials secure)
    // eslint-disable-next-line no-undef
    this.apiBaseUrl = process.env.API_BASE_URL;
  }

  /**
   * Verifies an address using FedEx Address Validation API (via PyxIQ backend)
   * @param {Object} address - The address to verify
   * @param {string} address.streetAddress - Street address
   * @param {string} address.city - City
   * @param {string} address.state - State
   * @param {string} address.zipCode - ZIP code
   * @returns {Promise<Object>} Verification result with suggested address or error
   */
  async verifyAddress(address) {
    try {
      // Basic validation first
      const isValidFormat = this.validateAddressFormat(address);
      if (!isValidFormat.valid) {
        return {
          isValid: false,
          isVerified: false,
          error: isValidFormat.error,
          suggestedAddress: null,
        };
      }

      // Get authorized API client with bearer token
      const authorizedApi = await getAuthClient();

      // Call PyxIQ backend API which will proxy to FedEx
      // The backend handles OAuth authentication and CORS
      const response = await authorizedApi.post(`${this.apiBaseUrl}/v1/address/verify`, {
        streetAddress: address.streetAddress,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
      });

      // Parse the new API response format
      // Response has status: "VALID", "UNVERIFIED", or "INVALID"
      const result = response.data;
      
      if (result.status === 'INVALID') {
        return {
          isValid: false,
          isVerified: false,
          error: result.error || result.message,
          suggestedAddress: null,
        };
      }
      
      if (result.status === 'UNVERIFIED') {
        return {
          isValid: true,
          isVerified: false,
          isExactMatch: false,
          suggestedAddress: result.suggestedAddress ? {
            streetAddress: result.suggestedAddress.streetAddress,
            city: result.suggestedAddress.city,
            state: result.suggestedAddress.state,
            zipCode: result.suggestedAddress.zipCode,
          } : null,
          message: result.message,
          confidence: 'MEDIUM',
        };
      }
      
      // status === 'VALID'
      return {
        isValid: true,
        isVerified: true,
        isExactMatch: true,
        suggestedAddress: result.suggestedAddress ? {
          streetAddress: result.suggestedAddress.streetAddress,
          city: result.suggestedAddress.city,
          state: result.suggestedAddress.state,
          zipCode: result.suggestedAddress.zipCode,
        } : null,
        message: result.message,
        confidence: 'HIGH',
      };
    } catch (error) {
      return {
        isValid: false,
        isVerified: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Address verification service temporarily unavailable',
        suggestedAddress: null,
      };
    }
  }

  /**
   * Basic address format validation
   * @private
   */
  validateAddressFormat(address) {
    if (!address.streetAddress || address.streetAddress.length < 5) {
      return { valid: false, error: 'Street address must be at least 5 characters long' };
    }

    if (!address.city || !/^[A-Za-z\s]+$/.test(address.city)) {
      return { valid: false, error: 'Invalid city format' };
    }

    if (!address.state || !/^[A-Z]{2}$/.test(address.state)) {
      return { valid: false, error: 'State must be a 2-letter abbreviation' };
    }

    if (!address.zipCode || !/^\d{5}(-\d{4})?$/.test(address.zipCode)) {
      return { valid: false, error: 'Invalid ZIP code format' };
    }

    return { valid: true };
  }

}

export default FedExAddressVerificationService;
